import React, { useEffect, useMemo, useState } from "react";
import { TextField as MUITextField } from "@mui/material";
import MuiAutocomplete from "@mui/material/Autocomplete";
import { AxiosResponse } from "axios";
import throttle from "lodash/throttle";
import queryString from "query-string";

import { axios } from "../api";
import type { AeronetV6ItemsResponse } from "../types";

export interface AircraftOption {
  id: number;
  registration: string | null;
}

interface Props {
  value: AircraftOption | null;
  onChange: (aircraft: AircraftOption | null) => void;
  label?: string;
  disabled?: boolean;
}

/**
 * The aircraft matching what the user has typed — GET /v6/aircraft, the aircraft
 * list's own route and default row set. Shared by the single and multi pickers.
 */
function useAircraftOptions(inputValue: string): AircraftOption[] {
  const [options, setOptions] = useState<AircraftOption[]>([]);

  const fetchAircraft = useMemo(
    () =>
      throttle((q: string, callback: (rows: AircraftOption[]) => void) => {
        const params = {
          filter: [
            JSON.stringify({
              field: "registration",
              op: "!=",
              value: "",
              model: "Aircraft",
            }),
          ],
          q,
        };
        axios
          .get("/v6/aircraft", {
            params,
            paramsSerializer: (p) => queryString.stringify(p),
          })
          .then((resp: AxiosResponse<AeronetV6ItemsResponse<AircraftOption>>) =>
            callback([...(resp.data.items ?? [])])
          )
          .catch(() => callback([]));
      }, 200),
    []
  );

  useEffect(() => {
    let active = true;
    fetchAircraft(inputValue, (rows) => {
      if (active) setOptions(rows);
    });
    return () => {
      active = false;
    };
  }, [inputValue, fetchAircraft]);

  return options;
}

/**
 * Aircraft FK autocomplete — resolves a registration label and hands the
 * caller the selected {id, registration} (write the scalar id yourself).
 * Module-local because the sors/rmas AircraftAutocomplete writes sors-shaped
 * side fields (lines.0.aircraft_id etc.) unsuitable for reuse.
 */
export default function AircraftAutocomplete({
  value,
  onChange,
  label = "Aircraft",
  disabled = false,
}: Props) {
  const [inputValue, setInputValue] = useState("");
  const options = useAircraftOptions(inputValue);

  return (
    <MuiAutocomplete<AircraftOption, false, false, false>
      fullWidth
      disabled={disabled}
      options={options}
      value={value}
      autoComplete
      includeInputInList
      onChange={(_e, next) => onChange(next)}
      onInputChange={(_e, next) => setInputValue(next)}
      getOptionLabel={(option) => option.registration ?? String(option.id)}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      renderInput={(params) => (
        <MUITextField {...params} label={label} fullWidth />
      )}
    />
  );
}

interface MultiProps {
  value: AircraftOption[];
  onChange: (aircraft: AircraftOption[]) => void;
  /** Aircraft that may not be picked (e.g. already linked) — left out of the options. */
  excludeIds?: number[];
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}

/** Several aircraft at once — the same options as AircraftAutocomplete, as chips. */
export function AircraftMultiAutocomplete({
  value,
  onChange,
  excludeIds,
  label = "Aircraft",
  disabled = false,
  error,
  helperText,
}: MultiProps) {
  const [inputValue, setInputValue] = useState("");
  const fetched = useAircraftOptions(inputValue);
  const options = useMemo(() => {
    const excluded = new Set(excludeIds ?? []);
    return fetched.filter((o) => !excluded.has(o.id));
  }, [fetched, excludeIds]);

  return (
    <MuiAutocomplete<AircraftOption, true, false, false>
      multiple
      disableCloseOnSelect
      fullWidth
      disabled={disabled}
      options={options}
      value={value}
      filterSelectedOptions
      onChange={(_e, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_e, next, reason) => {
        // keep the typed search after a pick, so several matches can be ticked in turn
        if (reason !== "reset") setInputValue(next);
      }}
      getOptionLabel={(option) => option.registration ?? String(option.id)}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      renderInput={(params) => (
        <MUITextField {...params} label={label} error={error} helperText={helperText} fullWidth />
      )}
    />
  );
}
